import { useEffect, useState } from 'react';
import { useLocale } from '../../context';
import { useResponsibleLimits } from '../../hooks/useResponsibleLimits';
import {
  RESPONSIBLE_PERIODS,
  buildEditablePeriodState,
  formatDepositDisplay,
  parseDepositValue,
} from '../../utils/responsibleLimits';
import MobileResponsibleDepositCurrent from './MobileResponsibleDepositCurrent';
import MobileResponsibleDepositInput from './MobileResponsibleDepositInput';
import MobileResponsibleLimitConfirm from './MobileResponsibleLimitConfirm';
import '../../styles/registration.css';

function MobileResponsibleToggle({ checked, onChange, label }) {
  return (
    <div className="mobile-responsible-toggle">
      <div className="mobile-responsible-toggle__label">{label}</div>
      <button
        type="button"
        className={`mobile-responsible-toggle__btn${checked ? ' is-on' : ''}`}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
      >
        <span className="mobile-responsible-toggle__circle" />
      </button>
    </div>
  );
}

function DepositLimitRow({period, state, saving, activeCurrent, onToggle, onValueChange, onInstall, onDelete, t: tr}) {
  const item = state[period];
  const open = item.enabled;
  const hasActive = Boolean(activeCurrent?.enabled && activeCurrent.value != null);
  const currentValue = hasActive ? parseDepositValue(formatDepositDisplay(activeCurrent.value)) : null;
  const inputValue = parseDepositValue(item.value);
  const valueChanged = hasActive && inputValue != null && inputValue !== currentValue;
  const showDelete = hasActive && !valueChanged;
  const showInstall = !hasActive || valueChanged;

  return (
    <div className={`mobile-responsible-limit limit--xmXyD${open ? ' is-open' : ''}`}>
      <div className="mobile-responsible-limit__controls controls--V3iG4">
        <MobileResponsibleToggle
          checked={item.enabled}
          onChange={(checked) => onToggle(period, checked)}
          label={tr(`account.responsible.${period}Limit`)}
        />
      </div>
      {open && (
        <div className="mobile-responsible-limit__enter enter _opened">
          <div className="enter__input">
            <MobileResponsibleDepositInput
              value={item.value}
              disabled={saving}
              onChange={(value) => onValueChange(period, value)}
            />
          </div>
          {showDelete ? (
            <button
              type="button"
              className="mobile-responsible-limit__delete enter__button"
              disabled={saving}
              onClick={() => onDelete(period)}
            >
              {tr('account.responsible.delete')}
            </button>
          ) : null}
          {showInstall ? (
            <button
              type="button"
              className="mobile-responsible-limit__install enter__button"
              disabled={saving}
              onClick={() => onInstall(period)}
            >
              {tr('account.responsible.install')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function formatDepositState(limitGroup) {
  const next = buildEditablePeriodState(limitGroup);
  RESPONSIBLE_PERIODS.forEach((period) => {
    if (next[period].value) {
      next[period].value = formatDepositDisplay(next[period].value);
    }
  });
  return next;
}

export default function MobileAccountResponsibleDeposit() {
  const _locale = useLocale();
  const tr = _locale.t;
  const { limits, loading, saving, msg, err, saveLimits } = useResponsibleLimits();
  const [state, setState] = useState(() => formatDepositState(limits.deposit));
  const [confirmTarget, setConfirmTarget] = useState(null);

  useEffect(() => {
    setState(formatDepositState(limits.deposit));
  }, [limits.deposit]);

  const handleToggle = async (period, enabled) => {
    setState((current) => ({
      ...current,
      [period]: { enabled, value: enabled ? current[period].value : '' },
    }));
    if (!enabled) {
      await saveLimits('deposit', { [period]: { enabled: false, value: null } });
    }
  };

  const handleValueChange = (period, value) => {
    setState((current) => ({
      ...current,
      [period]: { ...current[period], value },
    }));
  };

  const handleInstallClick = (period) => {
    const item = state[period];
    if (!item.enabled) return;
    const value = parseDepositValue(item.value);
    if (!value) return;
    setConfirmTarget({ period, action: 'change' });
  };

  const handleDeleteClick = (period) => {
    setConfirmTarget({ period, action: 'delete' });
  };

  const handleConfirmAction = async () => {
    if (!confirmTarget) return;
    if (confirmTarget.action === 'delete') {
      await saveLimits('deposit', { [confirmTarget.period]: { enabled: false, value: null } });
      setState((current) => ({
        ...current,
        [confirmTarget.period]: { enabled: false, value: '' },
      }));
    } else {
      const item = state[confirmTarget.period];
      const value = parseDepositValue(item.value);
      if (!value) {
        setConfirmTarget(null);
        return;
      }
      await saveLimits('deposit', { [confirmTarget.period]: { enabled: true, value } });
    }
    setConfirmTarget(null);
  };

  const confirmTitle = confirmTarget?.action === 'delete'
    ? tr('account.responsible.confirmDeleteTitle')
    : tr('account.responsible.confirmTitle');

  const confirmParagraphs = confirmTarget?.action === 'delete'
    ? []
    : [
      tr('account.responsible.confirmRemoveOrLower'),
      tr('account.responsible.confirmTightenImmediately'),
    ];

  if (loading) {
    return <div className="mobile-responsible-panel__loading">{tr('account.loading')}</div>;
  }

  return (
    <div className="mobile-responsible-panel mobile-responsible-panel--deposit widget deposit-limits">
      <MobileResponsibleDepositCurrent limits={limits.deposit} t={tr} />

      <h2 className="mobile-responsible-panel__caption">{tr('account.responsible.tabDeposit')}</h2>
      <p className="mobile-responsible-panel__desc">{tr('account.responsible.depositDescription')}</p>

      {err ? <div className="mobile-responsible-panel__error">{err}</div> : null}
      {msg ? <div className="mobile-responsible-panel__success">{msg}</div> : null}

      <div className="responsible-game-container mobile-responsible-panel__limits">
        {RESPONSIBLE_PERIODS.map((period) => (
          <DepositLimitRow
            key={period}
            period={period}
            state={state}
            saving={saving}
            activeCurrent={limits.deposit?.current?.[period]}
            onToggle={handleToggle}
            onValueChange={handleValueChange}
            onInstall={handleInstallClick}
            onDelete={handleDeleteClick}
            t={tr}
          />
        ))}
      </div>

      <MobileResponsibleLimitConfirm
        open={Boolean(confirmTarget)}
        title={confirmTitle}
        paragraphs={confirmParagraphs}
        confirmLabel={tr('account.responsible.confirmSet')}
        cancelLabel={tr('account.responsible.confirmCancel')}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmTarget(null)}
        loading={saving}
      />
    </div>
  );
}
